import React, { useState, useEffect } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Mail } from 'lucide-react';
import { normalizeEmail, getEmailError } from '../../utils/email';

/**
 * Reusable Email Input Component
 * 
 * Props:
 * - id: Input ID
 * - name: Input name
 * - value: Controlled value
 * - onChange: onChange handler
 * - onBlur: Optional onBlur handler
 * - label: Label text (optional)
 * - placeholder: Placeholder text
 * - required: Whether field is required (default: false)
 * - error: External error message (optional, overrides internal validation)
 * - className: Additional CSS classes
 * - iconClassName: Icon CSS classes
 * - showError: Whether to show error message (default: true)
 */
const EmailInput = ({
  id,
  name,
  value = '',
  onChange,
  onBlur,
  label,
  placeholder = 'Enter email address',
  required = false,
  error: externalError,
  className = '',
  iconClassName = '',
  showError = true,
  ...props
}) => {
  const [internalError, setInternalError] = useState(null);
  const [touched, setTouched] = useState(false);

  // Validate only when value is provided or field is touched
  useEffect(() => {
    // Only validate if value exists or field was touched
    if (touched || (value && value.trim())) {
      const error = getEmailError(value, required);
      setInternalError(error);
    } else {
      // If not required and empty, no error
      setInternalError(null);
    }
  }, [value, required, touched]);

  const handleChange = (e) => {
    onChange(e);
  };

  const handleBlur = (e) => {
    setTouched(true);
    // Normalize on blur
    const normalized = normalizeEmail(value);
    if (normalized !== value) {
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          name: name || id,
          value: normalized
        }
      };
      onChange(syntheticEvent);
    }
    
    const error = getEmailError(normalized || value, required);
    setInternalError(error);
    
    if (onBlur) {
      onBlur(e);
    }
  };

  const error = externalError || internalError;
  // Only show error if value is provided (not empty) or required and empty
  const showErrorMessage = showError && touched && error && (value.trim() || required);

  return (
    <div className="space-y-2">
      {label && (
        <Label htmlFor={id} className="text-white">
          {label}
          {required && <span className="text-red-400">*</span>}
        </Label>
      )}
      <div className="relative">
        <Mail className={`absolute left-3 top-3 h-4 w-4 text-gray-400 ${iconClassName}`} />
        <Input
          id={id}
          name={name || id}
          type="email"
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          className={`pl-10 bg-gray-800 border-gray-600 text-white ${error && showErrorMessage ? 'border-red-500' : ''} ${className}`}
          required={required}
          {...props}
        />
      </div>
      {showErrorMessage && (
        <p className="text-xs text-red-400">{error}</p>
      )}
    </div>
  );
};

export default EmailInput;

